import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/authService';

interface ServiceSuspendedBannerProps {
  companyName?: string;
  suspendedAt?: Date;
  overdueInvoices?: string[];
  /** 停止の理由。無料実証の期間終了なら 'free_trial_ended' */
  suspensionReason?: string;
}

export const ServiceSuspendedBanner: React.FC<ServiceSuspendedBannerProps> = ({
  companyName,
  suspendedAt,
  overdueInvoices,
  suspensionReason,
}) => {
  // 未払いでの停止と、無料実証の期間終了での停止で、お伝えすることが違う
  const isFreeTrialEnded = suspensionReason === 'free_trial_ended';
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleViewInvoices = () => {
    const currentPath = window.location.pathname;
    if (currentPath === '/admin/company-management') {
      const element = document.getElementById('invoice-history');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/admin/company-management');
      setTimeout(() => {
        const element = document.getElementById('invoice-history');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await AuthService.signOut();
      navigate('/admin-login');
    } catch (error) {
      console.error('ログアウトエラー:', error);
      setIsLoggingOut(false);
    }
  };

  return (
    <Alert
      severity="error"
      sx={{
        mb: 2,
        borderRadius: 2,
        '& .MuiAlert-icon': {
          fontSize: 28,
        },
      }}
      action={
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          {!isFreeTrialEnded && (
          <Button
            color="inherit"
            size="small"
            onClick={handleViewInvoices}
            sx={{
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            請求書を確認
          </Button>
          )}
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={handleLogout}
            disabled={isLoggingOut}
            sx={{
              fontWeight: 600,
              borderColor: 'rgba(255, 255, 255, 0.5)',
              color: 'inherit',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.8)',
              },
              '&:disabled': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {isLoggingOut ? 'ログアウト中...' : 'ログアウト'}
          </Button>
        </Box>
      }
    >
      <AlertTitle sx={{ fontWeight: 700, fontSize: 16 }}>
        {isFreeTrialEnded ? '無料でのご利用期間が終了しました' : 'サービス利用が停止されています'}
      </AlertTitle>
      <Box sx={{ fontSize: 14, lineHeight: 1.6 }}>
        {isFreeTrialEnded ? (
          <>
            無料でご利用いただける期間が終了したため、現在サービスをご利用いただけません。<br />
            引き続きご利用いただく場合は、お支払い情報のご登録をお願いいたします。<br />
            <Box component="span" sx={{ fontSize: 12, opacity: 0.9, display: 'block', mt: 0.5 }}>
              ※投稿などのデータは3か月間お預かりしております。その間にお手続きいただければ、そのままご利用を再開いただけます。
            </Box>
          </>
        ) : (
          <>
            お支払い状況の確認が取れていないため、現在サービスをご利用いただけません。<br />
            恐れ入りますが、お支払い状況のご確認をお願いいたします。<br />
            <Box component="span" sx={{ fontSize: 12, opacity: 0.9, display: 'block', mt: 0.5 }}>
              ※お支払い方法や処理状況により、反映までにお時間がかかる場合があります。
            </Box>
          </>
        )}
      </Box>
    </Alert>
  );
};

